import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Plus, Clock, CheckCircle, XCircle, Ban } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import CFiltersBar from '../components/shared/c-filters-bar';
import CTable from '../components/shared/c-table';
import { useAuth } from '../contexts/AuthContext';
import { LEAVE_STATUSES } from '../constants';
import { getStatusBadgeClasses, formatStatusText } from '../utils/statusUtils';
import { buildQueryParams } from '../utils/query';
import { formatDateOnly } from '../utils/dateUtils';
import SummaryCard from '../components/shared/summary-card';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';

const Leaves = () => {
  const { hasPermission, hasAnyResourceAction, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
    cancelled_count: 0,
  });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  // No page key here: the list opens on page 1, and every filter change goes
  // back to it (changeFilter below).
  const [statusFilter, setStatusFilter] = usePersistedState('leaves.status', '');
  const [typeFilter, setTypeFilter] = usePersistedState('leaves.type', '');
  const [awaitingFilter, setAwaitingFilter] = usePersistedState(
    'leaves.awaiting',
    ''
  );
  const [search, setSearch] = usePersistedState('leaves.search', '');

  const isOnboarded = !!user?.employee_id;
  const canCreate = hasPermission('leaves', 'create_own') && isOnboarded;
  const canReadAll = hasAnyResourceAction('leaves', [
    'read_all',
    'read_department',
  ]);

  // Who approves is the chain's call, not only leaves:approve: a lead with read_own
  // approves the requests of the people who report to them in the org chart. The
  // server says whether the caller approves anything and how much waits on them.
  // Until it answers, leaves:approve stands in, so an HR approver's stored filter
  // applies on the first load instead of after a second one.
  const [approvals, setApprovals] = useState(null);
  const canApprove = approvals
    ? approvals.is_approver
    : hasPermission('leaves', 'approve');
  const awaitingCount = approvals?.count || 0;

  // Only an approver gets the "Awaiting me" control, so only an approver may
  // send the param. The stored value outlives the session that picked it -
  // sessionStorage belongs to the browser tab, not to the user - so without
  // this an employee signing in after an approver inherits awaiting=me, sees
  // an empty list, and has no control on the page to clear it with.
  const awaiting = canApprove ? awaitingFilter : '';

  // Only the latest load may fill the list: a page click or a filter change while one
  // is in flight starts another, and a late response would overwrite the newer one.
  // Nor is the response's page written back: it is only ever the page asked for, and
  // writing it back let two loads in flight flip the page between them, refetching on
  // every flip.
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      setLoading(true);
      const params = buildQueryParams({
        status: statusFilter,
        leave_type_id: typeFilter,
        awaiting: awaiting || undefined,
        search: search || undefined,
        page,
        limit,
      });
      const [reqRes, statsRes] = await Promise.all([
        api.get('/api/leaves', { params }),
        api.get('/api/leaves/statistics/overview'),
      ]);
      if (!isCurrent()) return;
      setRequests(reqRes.data.data || []);
      setTotal(reqRes.data.total || 0);
      setStats(statsRes.data.data || {});
    } catch {
      if (isCurrent()) toast.error('Failed to load leave requests');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [statusFilter, typeFilter, awaiting, search, page, limit, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Narrowing the list from page 3 would otherwise request page 3 of a result that
  // may only have one page. Reset in the same update as the filter, so the change
  // loads once: an effect resetting it afterwards loaded the old page first.
  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    api
      .get('/api/leaves/awaiting/summary')
      .then((res) => setApprovals(res.data.data || { is_approver: false, count: 0 }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get('/api/leaves/types')
      .then((res) => setLeaveTypes(res.data.data || []))
      .catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / limit);

  const filteredRequests = requests;

  const employeeCol = {
    key: 'employee',
    header: 'Employee',
    cellClassName: 'font-medium',
    render: (r) => (
      <>
        {r.first_name} {r.last_name}
        {r.department_name && (
          <span className="block text-xs text-gray-400">
            {r.department_name}
          </span>
        )}
      </>
    ),
  };

  // The awaiting list is other people's requests even for a lead who can otherwise
  // only read their own, so it always says whose they are.
  const columns = [
    ...(canReadAll || awaiting ? [employeeCol] : []),
    {
      key: 'type',
      header: 'Type',
      // The one column allowed to wrap: a long leave-type name otherwise pushes
      // the last column past the card edge.
      cellClassName: 'whitespace-normal',
      render: (r) => r.leave_type_name,
    },
    {
      key: 'dates',
      header: 'Dates',
      render: (r) =>
        r.start_date && r.end_date ? (
          <>
            {formatDateOnly(r.start_date)} -{' '}
            {formatDateOnly(r.end_date)}
          </>
        ) : (
          '-'
        ),
    },
    {
      key: 'days',
      header: 'Days',
      render: (r) => (
        <span data-testid="leaves-cell-days">{r.business_days}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span
          data-testid="leaves-cell-status"
          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(r.status, 'leave')}`}
        >
          {formatStatusText(r.status)}
        </span>
      ),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cellClassName: 'text-gray-500',
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Link
          to={`/leaves/${r.id}`}
          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
        >
          View
        </Link>
      ),
    },
  ];

  const statCards = [
    {
      label: 'Pending',
      value: stats.pending_count || 0,
      icon: Clock,
      color: 'text-warning-600',
    },
    {
      label: 'Approved',
      value: stats.approved_count || 0,
      icon: CheckCircle,
      color: 'text-success-600',
    },
    {
      label: 'Rejected',
      value: stats.rejected_count || 0,
      icon: XCircle,
      color: 'text-danger-600',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled_count || 0,
      icon: Ban,
      color: 'text-gray-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Leave Requests</h1>
        </div>
        {canCreate && (
          <Link
            to="/leaves/new"
            data-testid="leaves-add-btn"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Link>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <SummaryCard
            key={label}
            variant="stat"
            icon={Icon}
            iconColor={color}
            label={label}
            value={value}
          />
        ))}
      </div>

      {/* Filters */}
      <CFiltersBar
        searchTestId="leaves-search"
        clearTestId="leaves-clear-filters"
        showSearch
        gridColsClass={canApprove ? 'md:grid-cols-5' : 'md:grid-cols-4'}
        searchTerm={search}
        onSearchChange={changeFilter(setSearch)}
        searchPlaceholder="Name or leave type…"
        filters={[
          // Only an approver has anything to be awaiting, so the filter only
          // appears for one. The column count above follows it.
          ...(canApprove
            ? [
                {
                  key: 'awaiting',
                  testId: 'leaves-awaiting-filter',
                  label: 'Approvals',
                  value: awaitingFilter,
                  onChange: changeFilter(setAwaitingFilter),
                  options: [
                    { value: '', label: 'All requests' },
                    {
                      value: 'me',
                      label: awaitingCount
                        ? `Awaiting me (${awaitingCount})`
                        : 'Awaiting me',
                    },
                  ],
                },
              ]
            : []),
          {
            key: 'status',
            testId: 'leaves-status-filter',
            label: 'Status',
            value: statusFilter,
            onChange: changeFilter(setStatusFilter),
            options: LEAVE_STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
            })),
          },
          {
            key: 'leaveType',
            testId: 'leaves-type-filter',
            label: 'Leave Type',
            value: typeFilter,
            onChange: changeFilter(setTypeFilter),
            options: [
              { value: '', label: 'All Types' },
              ...leaveTypes.map((t) => ({ value: t.id, label: t.name })),
            ],
          },
        ]}
        onClear={() => {
          setSearch('');
          setStatusFilter('');
          setTypeFilter('');
          setAwaitingFilter('');
          setPage(1);
        }}
      />

      {/* Table */}
      <CTable
        columns={columns}
        data={filteredRequests}
        loading={loading}
        rowKey="id"
        emptyMessage={
          <div className="text-gray-400">
            <Calendar className="mx-auto h-12 w-12 mb-3" />
            <p className="text-sm">No leave requests found</p>
          </div>
        }
        getRowProps={(r) => ({ 'data-testid': `leaves-row-${r.id}` })}
      />

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default Leaves;
